(function () {
  "use strict";

  const INLINE_BTN_ID = "tournamentAddTournamentInlineBtn";
  const MOBILE_BTN_ID = "mobileAddTournamentBtn";
  const MODAL_OPEN_CLASS = "plp-tournament-modal-open";

  let mountNode = null;
  let loadingProfiles = null;
  let preloadStarted = false;
  let profilesCacheByQuery = new Map();
  let activeProfiles = [];
  let searchDebounceTimer = null;
  let latestSearchRequestId = 0;

  function getLoginFallbackHref() {
    const returnUrl = `${window.location.pathname || "/"}${window.location.search || ""}`;
    return `/customer_authentication/login?return_to=${encodeURIComponent(returnUrl)}`;
  }

  function uniq(values) {
    return Array.from(new Set((values || []).map((value) => String(value || "").trim()).filter(Boolean)));
  }

  async function fetchProfiles(searchInput) {
    const query = String(searchInput || "").trim();
    if (query.length < 2) return [];

    const cacheKey = query.toLowerCase();
    if (profilesCacheByQuery.has(cacheKey)) {
      return profilesCacheByQuery.get(cacheKey) || [];
    }

    if (loadingProfiles) return loadingProfiles;

    const params = new URLSearchParams();
    params.set("app", "get-claimed-profiles");
    params.set("q", query);
    params.set("page", "1");
    params.set("pageSize", "100");

    loadingProfiles = fetch(`/a/app?${params.toString()}`, { method: "GET" })
      .then((response) => {
        if (!response.ok) throw new Error(`Failed to load profiles (${response.status})`);
        return response.json();
      })
      .then((payload) => {
        const profiles = Array.isArray(payload?.profiles) ? payload.profiles : [];
        const normalizedProfiles = profiles.map((profile) => ({
          id: profile.id,
          title: profile.title || profile.slug || "",
          slug: profile.slug || "",
          locationState: profile.locationState || "",
          locationCity: profile.locationCity || "",
        }));

        profilesCacheByQuery.set(cacheKey, normalizedProfiles);
        return normalizedProfiles;
      })
      .catch(() => {
        return [];
      })
      .finally(() => {
        loadingProfiles = null;
      });

    return loadingProfiles;
  }

  function ensureMountNode() {
    if (mountNode) return mountNode;
    mountNode = document.createElement("div");
    mountNode.className = "plp-tournament-modal-root";
    document.body.classList.add(MODAL_OPEN_CLASS);
    document.body.appendChild(mountNode);
    return mountNode;
  }

  function unmountModal() {
    document.body.classList.remove(MODAL_OPEN_CLASS);
    if (searchDebounceTimer) {
      window.clearTimeout(searchDebounceTimer);
      searchDebounceTimer = null;
    }
    latestSearchRequestId = 0;
    activeProfiles = [];
    if (!mountNode) return;
    if (window.preact && typeof window.preact.render === "function") {
      window.preact.render(null, mountNode);
    }
    mountNode.remove();
    mountNode = null;
  }

  function renderModal(profiles) {
    const safeProfiles = Array.isArray(profiles) ? profiles : [];
    const stateOptions = uniq(safeProfiles.map((profile) => profile.locationState));
    const cityOptions = uniq(safeProfiles.map((profile) => profile.locationCity));

    const node = ensureMountNode();
    window.renderFrontendTournamentForm(node, {
      isOpen: true,
      allProfiles: safeProfiles,
      locationStateOptions: stateOptions,
      locationCityOptions: cityOptions,
      onLocationSearch: handleLocationSearch,
      onClose: () => {
        unmountModal();
      },
      onSubmit: () => {
        window.location.reload();
      },
    });
  }

  function handleLocationSearch(criteria) {
    if (!mountNode) return;

    const query = String(criteria?.query || "").trim();
    if (query.length < 2) {
      if (activeProfiles.length > 0) {
        activeProfiles = [];
        renderModal(activeProfiles);
      }
      return;
    }

    if (searchDebounceTimer) {
      window.clearTimeout(searchDebounceTimer);
    }

    searchDebounceTimer = window.setTimeout(async () => {
      const requestId = ++latestSearchRequestId;
      const profiles = await fetchProfiles(query);
      if (!mountNode) return;
      if (requestId !== latestSearchRequestId) return;

      activeProfiles = Array.isArray(profiles) ? profiles : [];
      renderModal(activeProfiles);
    }, 220);
  }

  async function openTournamentModal(fallbackHref) {
    if (!window.isCustomerLoggedIn) {
      window.location.href = fallbackHref || getLoginFallbackHref();
      return;
    }

    if (!window.renderFrontendTournamentForm) {
      await waitForRenderer(2500);
    }

    if (!window.renderFrontendTournamentForm) {
      console.warn("Tournament modal renderer is not available yet");
      return;
    }

    activeProfiles = [];
    renderModal(activeProfiles);
  }

  function waitForRenderer(timeoutMs) {
    const timeout = Number(timeoutMs) > 0 ? Number(timeoutMs) : 2000;
    if (window.renderFrontendTournamentForm) return Promise.resolve(true);

    return new Promise((resolve) => {
      const started = Date.now();
      const timer = window.setInterval(() => {
        if (window.renderFrontendTournamentForm) {
          window.clearInterval(timer);
          resolve(true);
          return;
        }

        if (Date.now() - started >= timeout) {
          window.clearInterval(timer);
          resolve(false);
        }
      }, 50);
    });
  }

  function prewarmTournamentModal() {
    if (preloadStarted) return;
    preloadStarted = true;

    // Prewarm renderer readiness only; do not fetch profile lists until user searches.
    if (typeof window.requestIdleCallback === "function") {
      window.requestIdleCallback(() => {
        waitForRenderer(2500).catch(() => {});
      }, { timeout: 1200 });
    } else {
      window.setTimeout(() => {
        waitForRenderer(2500).catch(() => {});
      }, 300);
    }
  }

  function wireButton(button) {
    if (!button) return;
    const pointerPrewarm = () => {
      prewarmTournamentModal();
    };
    button.addEventListener("pointerenter", pointerPrewarm, { passive: true });
    button.addEventListener("touchstart", pointerPrewarm, { passive: true });
    button.addEventListener("click", (event) => {
      event.preventDefault();
      const href = button.getAttribute("data-fallback-href") || getLoginFallbackHref();
      openTournamentModal(href);
    });
  }

  function init() {
    const inlineBtn = document.getElementById(INLINE_BTN_ID);
    const mobileBtn = document.getElementById(MOBILE_BTN_ID);

    wireButton(inlineBtn);
    wireButton(mobileBtn);
    prewarmTournamentModal();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
